import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { CrudStyles } from '../../shared/Crud';
import driverService from '../../../services/driverService';
import MessageModal from '../../shared/MessageModal';
import DeliveryDriverSelectorModal from './DeliveryDriverSelectorModal';

const { SectionTitle, Button } = CrudStyles;

const DriverSection = styled.section`
    border-radius: 12px;
    padding: 24px;
    margin-top: 20px;
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    background: ${({ theme }) =>
        theme?.colors?.primary?.light ? `${theme.colors.primary.light}08` : 'rgba(59, 130, 246, 0.04)'};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    border: 1px solid
        ${({ theme }) =>
            theme?.colors?.primary?.light ? `${theme.colors.primary.light}40` : 'rgba(59, 130, 246, 0.12)'};
`;

const SectionTitleSoft = styled(SectionTitle)`
    font-size: 15px;
    font-weight: 600;
    color: ${({ theme }) => theme?.colors?.gray?.[800] || '#374151'};
    margin-bottom: 0;
    margin-top: 0;
`;

const HelpText = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${({ theme }) => theme?.colors?.gray?.[600] || '#6b7280'};
    max-width: 42rem;
`;

const HintError = styled.span`
    color: #b91c1c;
    font-size: 13px;
`;

const CostCenterGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 12px;
`;

const CostCenterCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px 14px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    min-width: 0;
`;

const CostCenterCellLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b;
`;

const CostCenterCellValue = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: #0f172a;
    word-break: break-word;
`;

const CostCenterEmptyState = styled.div`
    padding: 18px 16px;
    text-align: center;
    border-radius: 10px;
    border: 1px dashed #cbd5e1;
    background: rgba(255, 255, 255, 0.7);
    color: #94a3b8;
    font-size: 14px;
    font-weight: 500;
`;

const CostCenterActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: flex-end;
    align-items: center;
`;

export default function DeliveryDriverSection({
    deliveryId,
    customerIdForApi,
    deliveryEntityCustomerId,
    driverId,
    driverName = '',
    onDriverChange,
    /** Quando true, não chama API: apenas atualiza estado via onDriverChange (ex.: rota ainda não persistida). */
    localSelectionOnly = false,
    sectionTitle = 'Motorista da entrega',
    helpText = 'Use o botão abaixo para abrir a lista paginada de motoristas. A seleção é salva ao escolher um na lista; é possível remover o motorista pelo mesmo modal.',
}) {
    const [messageModal, setMessageModal] = useState({ open: false, title: 'Aviso', message: '' });
    const [modalOpen, setModalOpen] = useState(false);
    const [patching, setPatching] = useState(false);

    const effectiveCustomerId = useMemo(() => {
        const a = customerIdForApi != null && String(customerIdForApi).trim() !== '' ? customerIdForApi : null;
        const b =
            deliveryEntityCustomerId != null && String(deliveryEntityCustomerId).trim() !== ''
                ? deliveryEntityCustomerId
                : null;
        return a ?? b;
    }, [customerIdForApi, deliveryEntityCustomerId]);

    const customerMissing = effectiveCustomerId == null || String(effectiveCustomerId).trim() === '';

    const openModal = () => {
        if (localSelectionOnly) {
            setModalOpen(true);
            return;
        }
        if (!deliveryId) {
            setMessageModal({
                open: true,
                title: 'Aviso',
                message: 'A entrega precisa estar criada antes de atribuir motorista.',
            });
            return;
        }
        if (customerMissing) {
            setMessageModal({
                open: true,
                title: 'Aviso',
                message:
                    'Não foi possível identificar o cliente desta entrega (customerId). Abra o detalhe a partir da listagem ou garanta que a entrega retorne o identificador do cliente.',
            });
            return;
        }
        setModalOpen(true);
    };

    const handleSelectDriver = async (driver) => {
        if (driver?.id == null) return;
        if (localSelectionOnly) {
            onDriverChange?.({
                driverId: driver.id,
                driverName: driver.name ?? `Motorista #${driver.id}`,
            });
            setModalOpen(false);
            return;
        }
        if (customerMissing || !deliveryId) return;
        setPatching(true);
        try {
            await driverService.setDeliveryDriver(deliveryId, effectiveCustomerId, driver.id);
            onDriverChange?.({
                driverId: driver.id,
                driverName: driver.name ?? `Motorista #${driver.id}`,
            });
            setModalOpen(false);
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || 'Não foi possível atualizar o motorista da entrega.',
            });
        } finally {
            setPatching(false);
        }
    };

    const handleClearDriver = async () => {
        if (localSelectionOnly) {
            onDriverChange?.({ driverId: null, driverName: '' });
            setModalOpen(false);
            return;
        }
        if (customerMissing || !deliveryId) return;
        setPatching(true);
        try {
            await driverService.setDeliveryDriver(deliveryId, effectiveCustomerId, null);
            onDriverChange?.({ driverId: null, driverName: '' });
            setModalOpen(false);
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || 'Não foi possível remover o motorista da entrega.',
            });
        } finally {
            setPatching(false);
        }
    };

    const buttonLabel =
        driverId != null && !Number.isNaN(Number(driverId))
            ? 'Selecionar/alterar motorista'
            : 'Selecionar motorista';

    return (
        <>
            <DriverSection aria-labelledby="delivery-driver-title">
                <div>
                    <SectionTitleSoft id="delivery-driver-title">{sectionTitle}</SectionTitleSoft>
                    <HelpText>{helpText}</HelpText>
                </div>

                {!localSelectionOnly && customerMissing ? (
                    <HintError>
                        É necessário o customerId da entrega para usar esta função. Recarregue a página ou abra esta
                        entrega a partir da listagem (com empresa identificada).
                    </HintError>
                ) : null}

                {driverId != null && !Number.isNaN(Number(driverId)) ? (
                    <CostCenterGrid>
                        <CostCenterCell>
                            <CostCenterCellLabel>ID</CostCenterCellLabel>
                            <CostCenterCellValue>{String(driverId)}</CostCenterCellValue>
                        </CostCenterCell>
                        <CostCenterCell>
                            <CostCenterCellLabel>Nome</CostCenterCellLabel>
                            <CostCenterCellValue>{driverName?.trim() || '—'}</CostCenterCellValue>
                        </CostCenterCell>
                    </CostCenterGrid>
                ) : (
                    <CostCenterEmptyState>Nenhum motorista atribuído</CostCenterEmptyState>
                )}

                <CostCenterActions>
                    <Button
                        type="button"
                        variant="outline"
                        disabled={
                            patching ||
                            (!localSelectionOnly && (!deliveryId || customerMissing))
                        }
                        onClick={openModal}
                        title="Abrir lista de motoristas"
                    >
                        {patching ? 'Salvando…' : buttonLabel}
                    </Button>
                </CostCenterActions>
            </DriverSection>

            <DeliveryDriverSelectorModal
                open={modalOpen}
                currentDriverId={driverId}
                onClose={() => !patching && setModalOpen(false)}
                onSelectDriver={handleSelectDriver}
                onClearDriver={handleClearDriver}
                isSubmitting={patching}
                removalConfirmMessage={
                    localSelectionOnly
                        ? 'Deseja remover o motorista desta rota?'
                        : undefined
                }
                removeButtonTitle={
                    localSelectionOnly ? 'Remover motorista da rota' : undefined
                }
            />

            {messageModal.open && (
                <MessageModal
                    title={messageModal.title}
                    message={messageModal.message}
                    onClose={() => setMessageModal((m) => ({ ...m, open: false }))}
                />
            )}
        </>
    );
}
